/**
 * Wall-oven single vs double classification — one source of truth, shared by
 * kitchen post-processing (which stamps `subtype`), the 3D procedural renderer
 * and the elevation schematics.
 *
 * It lived inside `WallOvenSchematic.jsx` and so only elevations could see it:
 * the 3D renderer drew a single cavity no matter how tall the oven was. Same
 * class of split-brain as the appliance type resolver — one path knew a fact
 * the other ignored.
 */

const INCHES_TO_METERS = 0.0254;

/**
 * Height at or above which an oven reads as double-stacked.
 *
 * Real single wall-oven cavities top out around 30"; real doubles start around
 * 45" (a 27" double unit) and run to 53". 40" sits in the empty band between
 * those two clusters, so no real appliance lands near the boundary and scan
 * noise can't flip the answer.
 *
 * This was 44" while the check lived in the elevation schematic, which put the
 * threshold inside the double range — a 43.8" scanned oven (structure
 * FC5CBAFF, a 43.8" cavity sitting on a 20" base cabinet) classified as a
 * single by 0.2".
 */
const WALL_OVEN_DOUBLE_HEIGHT_THRESHOLD = 40 * INCHES_TO_METERS;

/**
 * @param {Object} [obj] - the oven object; an explicit `subtype`
 *   ('single' | 'double' | 'double-stacked') always wins over the heuristic.
 * @param {number} [heightMeters] - the oven's height. A missing/unusable
 *   height falls back to 'single' rather than guessing double.
 * @returns {'single'|'double'}
 */
function resolveWallOvenVariant(obj = {}, heightMeters) {
  const subtype = obj && obj.subtype;
  if (subtype === 'double' || subtype === 'double-stacked') return 'double';
  if (subtype === 'single') return 'single';
  if (!Number.isFinite(heightMeters)) return 'single';
  return heightMeters >= WALL_OVEN_DOUBLE_HEIGHT_THRESHOLD
    ? 'double'
    : 'single';
}

module.exports = {
  WALL_OVEN_DOUBLE_HEIGHT_THRESHOLD,
  resolveWallOvenVariant,
};
